import { authApi } from '@/api/auth'

const REFRESH_AHEAD_MS = 5 * 60 * 1000
export const SESSION_EXPIRED_EVENT = 'hosin:session-expired'

type JwtPayload = { exp?: number }
type RefreshResponse = { token: string; expiration: string }
export type RefreshAttempt = { token: string | null; sessionExpired: boolean }

let refreshPromise: Promise<RefreshAttempt> | null = null
let expirationEventSent = false

const decodePayload = (token: string): JwtPayload | null => {
  try {
    const encoded = token.split('.')[1]
    if (!encoded) return null
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    return JSON.parse(atob(padded)) as JwtPayload
  } catch {
    return null
  }
}

export const getTokenExpirationMs = (token: string | null) => {
  if (!token) return null
  const exp = decodePayload(token)?.exp
  return typeof exp === 'number' ? exp * 1000 : null
}

export const shouldRefreshToken = (token: string | null) => {
  const expiration = getTokenExpirationMs(token)
  return expiration === null || expiration - Date.now() <= REFRESH_AHEAD_MS
}

export const clearStoredSession = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
  localStorage.removeItem('username')
  localStorage.removeItem('branchName')
}

export const notifySessionExpired = () => {
  if (expirationEventSent) return
  expirationEventSent = true
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
}

export const markSessionActive = () => {
  expirationEventSent = false
}

export const refreshAccessTokenResult = (): Promise<RefreshAttempt> => {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const response = await authApi.refresh()
      if (!response.ok)
        return {
          token: null,
          sessionExpired: response.status === 401 || response.status === 403,
        }
      const data = (await response.json()) as RefreshResponse
      if (!data.token) return { token: null, sessionExpired: false }
      localStorage.setItem('token', data.token)
      markSessionActive()
      return { token: data.token, sessionExpired: false }
    } catch {
      return { token: null, sessionExpired: false }
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}
export const refreshAccessToken = async () => (await refreshAccessTokenResult()).token

export const ensureAccessToken = async () => {
  const token = localStorage.getItem('token')
  if (!token || shouldRefreshToken(token)) return refreshAccessToken()
  return token
}
